// Drag-resize for the right detail sidebars (desktop split layout).
//
// Every right panel — Jane, contact detail, message detail, events, dating —
// shares the .detail-sidebar chrome but is created in different places
// (server-rendered partials + several JS controllers), so this is a single
// delegated listener rather than per-panel wiring: dragging the sidebar's
// left edge sets --detail-sidebar-width on :root, which the shared CSS rule
// reads. The width persists in localStorage (restored pre-paint by the
// layout's head script) and applies to all pages. Double-click the edge to
// reset to the default width.

const KEY = "emailpa-sidebar-width"
const MIN_WIDTH = 320
// Hit-zone from the sidebar's left edge — the framed gutter (see
// --detail-frame-pad-left in _desktop_nav.scss). A hair wider than the visual
// gutter so the very edge is still grabbable.
const EDGE = 18

const root = document.documentElement

// The gray gutter holds an injected grip (pill indicator + tooltip). The
// sidebars are built in many places (server partials + several JS controllers),
// so rather than add the markup everywhere we inject it once per sidebar here.
const ensureGrip = (sidebar) => {
    if (onPersonaPage(sidebar)) return
    if (onProfileStagePage(sidebar) && !isChatPane(sidebar)) {
        sidebar.querySelector(":scope > .detail-sidebar-grip")?.remove()
        return
    }
    if (sidebar.querySelector(":scope > .detail-sidebar-grip")) return
    const grip = document.createElement("div")
    grip.className = "detail-sidebar-grip"
    grip.setAttribute("aria-hidden", "true")
    grip.dataset.tip = "Drag to resize"
    const bar = document.createElement("span")
    bar.className = "detail-sidebar-grip-bar"
    grip.appendChild(bar)
    sidebar.prepend(grip)
}

// The persona character card is resized by its own grip (persona_layout.js),
// never by this one. The docked chat panes - the app's Messages sidebar and a
// profile's chat panel (visitors' too) - share the gutter frame and the same
// width (--messages-dock-width reads --detail-sidebar-width), so they resize
// the same way; on a profile page that also moves the chat's column.
const CHAT_PANE = ".messages-drawer.messages-drawer--sidebar"
const RESIZABLE = `.detail-sidebar:not(.detail-sidebar--persona), ${CHAT_PANE}`
const isChatPane = (el) => !!el.matches?.(CHAT_PANE)

// The persona page runs its own resize logic (persona_layout.js) for both of
// its panes, so this generic single-width resizer must keep its hands off it —
// otherwise both would grip the Jane pane and fight over the width. A sidebar is
// "on the persona page" when its .assistant-phone row also holds the character
// card. Every other page has no persona card, so this returns false there and
// their behavior is byte-for-byte unchanged.
const onPersonaPage = (el) => !!el.closest?.(".assistant-phone")?.querySelector(".detail-sidebar--persona")
const onProfileStagePage = (el) => !!el.closest?.(".public-profile--sidepanel")

const scanGrips = () => document.querySelectorAll(RESIZABLE).forEach(ensureGrip)

// Server-rendered sidebars exist at load; JS-built ones (msg detail, contact
// detail, events, dating) appear later, so watch for them too.
scanGrips()
document.addEventListener("turbo:load", scanGrips)
new MutationObserver((mutations) => {
    for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
            if (node.nodeType !== 1) continue
            if (node.matches?.(RESIZABLE)) ensureGrip(node)
            node.querySelectorAll?.(RESIZABLE).forEach(ensureGrip)
        }
    }
}).observe(document.documentElement, { childList: true, subtree: true })

const maxWidth = () => Math.round(window.innerWidth * 0.6)
const clamp = (w) => Math.min(Math.max(w, MIN_WIDTH), maxWidth())

const apply = (w) => root.style.setProperty("--detail-sidebar-width", `${w}px`)
const reset = () => {
    root.style.removeProperty("--detail-sidebar-width")
    localStorage.removeItem(KEY)
}

// The head script restores before first paint; this covers module-only paths
// (e.g. storage changed in another tab before a Turbo visit).
const saved = parseInt(localStorage.getItem(KEY), 10)
if (saved) apply(clamp(saved))

const onEdge = (event) => {
    const sidebar = event.target.closest?.(RESIZABLE)
    if (!sidebar || onPersonaPage(sidebar) || (onProfileStagePage(sidebar) && !isChatPane(sidebar))) return null
    if (isChatPane(sidebar) && sidebar.classList.contains("messages-drawer--fullscreen")) return null
    // Children can cover the strip; measure from the sidebar's box instead of
    // trusting the event target.
    const distance = event.clientX - sidebar.getBoundingClientRect().left
    return distance >= 0 && distance <= EDGE ? sidebar : null
}

let dragging = null

document.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return
    const sidebar = onEdge(event)
    if (!sidebar) return
    dragging = sidebar
    event.preventDefault()
    root.classList.add("sidebar-resizing")
})

document.addEventListener("pointermove", (event) => {
    if (!dragging) return
    apply(clamp(Math.round(dragging.getBoundingClientRect().right - event.clientX)))
})

const endDrag = () => {
    if (!dragging) return
    dragging = null
    root.classList.remove("sidebar-resizing")
    const width = parseInt(root.style.getPropertyValue("--detail-sidebar-width"), 10)
    if (width) localStorage.setItem(KEY, String(width))
}

document.addEventListener("pointerup", endDrag)
document.addEventListener("pointercancel", endDrag)

document.addEventListener("dblclick", (event) => {
    if (onEdge(event)) reset()
})
